import { Line } from 'react-chartjs-2';

import { formatMoney } from '../../utils/helpers/aiSpendFormat';
import { colorForKey, registerCharts } from './chartSetup';
import useChartTheme from './useChartTheme';

registerCharts();

/**
 * `datasets` is `[{ key, label, data: number[], color? }]`.
 *
 * Callers cannot pass raw chart.js config, so nobody can hand a chart a string
 * field and get a silently flat line: the dev assertion below catches it at the
 * point of use.
 */
export default function LineChart({
  labels,
  datasets,
  valueFormatter = formatMoney,
  stacked = false,
  ariaLabel,
}) {
  const theme = useChartTheme();

  if (process.env.NODE_ENV !== 'production') {
    datasets.forEach((set) =>
      set.data.forEach((point) => {
        if (point !== null && !Number.isFinite(point)) {
          // eslint-disable-next-line no-console
          console.error(
            `Chart "${ariaLabel}" dataset "${set.label}": non-numeric data point`,
            point,
          );
        }
      }),
    );
  }

  const data = {
    labels,
    datasets: datasets.map((set, index) => ({
      label: set.label,
      data: set.data,
      borderColor: set.color || colorForKey(set.key, index),
      backgroundColor: `${set.color || colorForKey(set.key, index)}22`,
      fill: datasets.length === 1,
      tension: 0.25,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 120,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        display: datasets.length > 1,
        labels: { color: theme.tick, boxWidth: 10 },
      },
      tooltip: {
        ...theme.tooltip,
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${valueFormatter(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { stacked, ticks: { color: theme.tick }, grid: { display: false } },
      y: {
        stacked,
        ticks: { color: theme.tick, callback: (value) => valueFormatter(value) },
        grid: { color: theme.grid },
      },
    },
  };

  // Tick and grid colours are read when the scale is built, so a theme flip
  // needs a remount rather than an options update.
  return (
    <Line
      key={theme.isDark ? 'dark' : 'light'}
      data={data}
      options={options}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
